import { SIM_DT, SIM_HZ } from '../core/fixedLoop';
import { emptyInput, type InputFrame } from '../core/input';
import { botPickUpgrade } from '../core/bot';
import type { ReplayRecorder } from '../meta/replay';
import { ERA_TRANSITION_SECONDS } from '../render/transitionTiming';
import type { Sim, SimCommand } from '../sim/sim';
import { deserializeSnapshot, restore, serializeSnapshot, snapshot } from '../sim/snapshot';
import type { NetChannel } from './channel';
import { BUTTON_ACTION, BUTTON_DASH, dequantizeInput, EMPTY_WIRE_INPUT, isSimCommand, isWireInput, quantizeInput, type WireInput } from './protocol';

/**
 * HOST-RELAYED DETERMINISTIC LOCKSTEP (docs/MULTIPLAYER.md § Step 2)
 *
 * Every peer owns an identical `Sim` built from the same stored RunConfig.
 * Time is counted in *net frames* (one per fixed 60 Hz step, advancing through
 * every run phase — level-ups and stage clears included — unlike `world.tick`).
 *
 *   guest ── { in, f, input, commands } ──▶ host
 *   host  ── { frame, f, inputs[], commands[] } ──▶ every guest
 *
 * The host is the only authority on *which* input each player had on frame F:
 * it waits for every connected player's input for F (up to GRACE_MS), then
 * broadcasts the bundle and executes it; guests execute bundles in order and
 * nothing else. Because every peer runs exactly the host's bundles, a dropped
 * or late player can never fork the timeline — the host substitutes their last
 * movement (edges and commands carry forward to the next open frame) and, if
 * they vanish, feeds empty input from then on.
 *
 * Local input is scheduled INPUT_DELAY frames ahead so ordinary network
 * latency is hidden; a peer never samples more than INPUT_DELAY + MAX_AHEAD
 * frames beyond what it has executed, so a slow link degrades into extra input
 * delay rather than divergence. Every HASH_INTERVAL frames guests report
 * `Sim.hashState()`; on a mismatch (e.g. a Math.* difference between browser
 * engines) the host sends a full snapshot and the guest restores it.
 */

export const INPUT_DELAY = 4;
export const MAX_AHEAD = 16;
/** Bounds for adaptive input delay (frames). */
export const MIN_DELAY = 3;
export const MAX_DELAY = 9;
export const GRACE_MS = 150;
/** Silence before a guest is dropped (closed connections drop immediately). */
export const DROP_AFTER_MS = 15000;
/** Extra silence allowed right after a rejoin while the page restores the snapshot. */
const REJOIN_GRACE_MS = 10000;
export const HASH_INTERVAL = 120;
export const MAX_CATCHUP = 4;
/** Deterministic pause for the 3D time jump: identical on every peer. */
export const JUMP_HOLD_FRAMES = Math.round(ERA_TRANSITION_SECONDS * SIM_HZ);
/** Minimum gap between two resyncs sent to the same guest. */
const RESYNC_COOLDOWN_MS = 2000;
/** Ignore inputs scheduled absurdly far ahead (a confused or hostile peer). */
const MAX_FUTURE_FRAMES = 600;
/** On-time frames before a lagging player is waited for again. */
const RECOVER_FRAMES = 45;
/** Guest playout buffer: frames to queue before resuming after running dry. */
const PLAYOUT_FRAMES = 2;
/** …or resume anyway once we've waited this long with something queued. */
const REBUFFER_MAX_MS = 60;

export interface LockstepStats {
  frames: number;
  /** Guest: times the playout buffer ran dry and re-buffered. */
  rebuffers: number;
  /** Frames where the host stood in for a late player's input. */
  substitutions: number;
  /** Snapshot resyncs sent (host) or applied (guest). */
  resyncs: number;
  /** Hash reports that disagreed with the host. */
  desyncs: number;
}

interface Entry {
  i: WireInput;
  c: SimCommand[];
}

type Bundle = { i: WireInput[]; c: [number, SimCommand][] };

abstract class LockstepCore {
  /** Next net frame to execute. */
  nextExec = 0;
  /** Next net frame this peer will assign its local input to. */
  localFrame: number;
  /** Frames < holdUntil skip `sim.tick` (time-jump transition). */
  holdUntil = 0;
  readonly stats: LockstepStats = { frames: 0, rebuffers: 0, substitutions: 0, resyncs: 0, desyncs: 0 };
  /** Optional replay recorder; only fed frames that actually advanced the world. */
  recorder: ReplayRecorder | null = null;
  /** Called for every command a bundle carried, with whether the sim accepted it. */
  onCommand: ((player: number, cmd: SimCommand, ok: boolean) => void) | null = null;
  /** Called when an era advance starts the deterministic time-jump hold. */
  onEraJump: (() => void) | null = null;
  protected pendingLocal: SimCommand[] = [];
  protected heldEdges = 0;
  private readonly frames: InputFrame[];

  constructor(readonly sim: Sim, readonly localIndex: number, public delay: number, protected readonly now: () => number) {
    this.localFrame = delay;
    this.frames = Array.from({ length: sim.config.playerCount }, () => emptyInput());
  }

  get playerCount(): number {
    return this.sim.config.playerCount;
  }

  /** True while the deterministic time-jump hold is in effect. */
  get holding(): boolean {
    return this.nextExec < this.holdUntil;
  }

  /** Queue a phase-changing command from the local player; it rides on the next input frame. */
  issue(cmd: SimCommand): void {
    this.pendingLocal.push(cmd);
  }

  /**
   * Change the input delay mid-run. Safe at any time: it only changes how far
   * ahead this peer schedules its own input; the bundles stay authoritative.
   */
  setDelay(delay: number): void {
    const next = Math.max(MIN_DELAY, Math.min(MAX_DELAY, Math.round(delay)));
    if (next > this.delay) this.fillPending = true;
    this.delay = next;
  }

  /** Set by setDelay when the delay grows: the next sampled input also fills the widened gap once. */
  protected fillPending = false;

  /** Quantize local input, carrying button edges across frames we couldn't send yet. */
  protected takeLocal(local: InputFrame): WireInput | null {
    const w = quantizeInput(local);
    if (this.localFrame >= this.nextExec + this.delay + MAX_AHEAD) {
      this.heldEdges |= w[2] & (BUTTON_DASH | BUTTON_ACTION);
      return null;
    }
    w[2] |= this.heldEdges;
    this.heldEdges = 0;
    return w;
  }

  protected execute(f: number, bundle: Bundle): void {
    const sim = this.sim;
    // Era travel happens inside the sim (walking into a portal) or via a legacy
    // advanceStage command; either way every peer sees the serial change on the
    // same frame and holds for the time jump together.
    const serial = sim.run.advanceSerial;
    for (const [player, cmd] of bundle.c) {
      const tick = sim.world.tick;
      const ok = sim.applyCommand(cmd);
      if (ok) this.recorder?.command(tick, cmd);
      this.onCommand?.(player, cmd, ok);
    }
    if (f >= this.holdUntil) {
      for (let p = 0; p < this.frames.length; p++) dequantizeInput(bundle.i[p] ?? EMPTY_WIRE_INPUT, this.frames[p]);
      const before = sim.world.tick;
      sim.tick(SIM_DT, this.frames);
      // Replays only see steps that advanced the world, exactly like solo recording.
      if (sim.world.tick !== before) this.recorder?.frame(this.frames);
    }
    if (sim.run.advanceSerial !== serial && sim.run.lastAdvanceEra) {
      this.holdUntil = f + 1 + JUMP_HOLD_FRAMES;
      this.onEraJump?.();
    }
    this.nextExec = f + 1;
    this.stats.frames++;
    if (f > 0 && f % HASH_INTERVAL === 0) this.hashPoint(f, sim.hashState());
  }

  protected abstract hashPoint(f: number, hash: number): void;
}

/* -------------------------------------------------------------------------- */
/* Host                                                                        */
/* -------------------------------------------------------------------------- */

export class LockstepHost extends LockstepCore {
  readonly dropped: boolean[];
  /** Called when a guest is dropped (disconnect or timeout). */
  onPlayerLeft: ((player: number, reason: string) => void) | null = null;
  private store: Map<number, Entry>[];
  private last: WireInput[];
  private carryEdges: number[];
  private carryCmds: SimCommand[][];
  private lastHeard: number[];
  private lastResyncAt: number[];
  private waitStart: number | null = null;
  /** Players currently too slow to wait for (hysteresis: RECOVER_FRAMES on-time frames clears it). */
  private lagging: boolean[];
  private onTime: number[];
  private hashes = new Map<number, number>();
  /** Players the host is currently waiting on (for a "waiting for …" overlay). */
  waitingOn: number[] = [];
  private stepCount = 0;

  /** `peers[i]` is guest i's channel; index 0 (the host) is null. */
  constructor(sim: Sim, private readonly peers: (NetChannel | null)[], now: () => number, delay = INPUT_DELAY) {
    super(sim, 0, delay, now);
    const n = sim.config.playerCount;
    this.dropped = Array.from({ length: n }, (_, i) => i !== 0 && !peers[i]);
    this.store = Array.from({ length: n }, () => new Map());
    this.last = Array.from({ length: n }, () => [...EMPTY_WIRE_INPUT] as WireInput);
    this.carryEdges = Array.from({ length: n }, () => 0);
    this.carryCmds = Array.from({ length: n }, () => []);
    const t = now();
    this.lastHeard = Array.from({ length: n }, () => t);
    this.lastResyncAt = Array.from({ length: n }, () => -Infinity);
    this.lagging = Array.from({ length: n }, () => false);
    this.onTime = Array.from({ length: n }, () => 0);
  }

  /** Route a lockstep message from guest `player`. */
  receive(player: number, msg: unknown): void {
    if (player <= 0 || player >= this.playerCount || this.dropped[player] || !msg || typeof msg !== 'object') return;
    this.lastHeard[player] = this.now();
    const m = msg as Record<string, unknown>;
    if (m.t === 'in') {
      const f = m.f;
      if (!Number.isInteger(f) || !isWireInput(m.i)) return;
      const cmds = (Array.isArray(m.c) ? m.c : []).filter((c): c is SimCommand => isSimCommand(c) && this.mayIssue(player, c));
      const frame = f as number;
      if (frame < this.nextExec) {
        // Late: the host already stood in for this frame. Keep the newest
        // movement and carry the edges/commands into the next open frame.
        this.last[player] = m.i;
        this.carryEdges[player] |= m.i[2];
        this.carryCmds[player].push(...cmds);
      } else if (frame < this.nextExec + MAX_FUTURE_FRAMES) {
        this.store[player].set(frame, { i: m.i, c: cmds });
      }
    } else if (m.t === 'hash') {
      const f = m.f as number;
      const mine = this.hashes.get(f);
      if (mine === undefined || mine === m.h) return;
      this.stats.desyncs++;
      this.resync(player);
    } else if (m.t === 'leave') {
      this.drop(player, 'left the run');
    }
  }

  /** Guests may only act for themselves, and only the host may advance stages. */
  private mayIssue(player: number, c: SimCommand): boolean {
    return c.type !== 'advanceStage' && c.player === player;
  }

  /** The transport reported guest `player` gone (ignored if it's a stale link for a slot that was since rejoined). */
  peerClosed(player: number, channel?: NetChannel): void {
    if (channel && this.peers[player] && this.peers[player] !== channel) return;
    this.drop(player, 'disconnected');
  }

  /**
   * A dropped player reconnected: give them their slot back and send a full
   * snapshot. They start "lagging" (stood in for, never waited on) until their
   * input has been on time for RECOVER_FRAMES, so the rejoin can't hitch anyone.
   */
  readmit(player: number, channel: NetChannel): boolean {
    if (player <= 0 || player >= this.playerCount || !this.dropped[player]) return false;
    this.peers[player] = channel;
    this.dropped[player] = false;
    this.lastHeard[player] = this.now() + REJOIN_GRACE_MS;
    this.lagging[player] = true;
    this.onTime[player] = 0;
    this.store[player].clear();
    this.carryCmds[player] = [];
    this.carryEdges[player] = 0;
    this.last[player] = [...EMPTY_WIRE_INPUT] as WireInput;
    this.lastResyncAt[player] = -Infinity;
    this.resync(player);
    return true;
  }

  /** Is this player currently being stood in for? (debug panel) */
  isLagging(player: number): boolean {
    return this.lagging[player] ?? false;
  }

  drop(player: number, reason: string): void {
    if (player <= 0 || player >= this.playerCount || this.dropped[player]) return;
    this.dropped[player] = true;
    this.store[player].clear();
    this.carryCmds[player] = [];
    this.carryEdges[player] = 0;
    const ch = this.peers[player];
    this.peers[player] = null;
    ch?.close(reason);
    this.onPlayerLeft?.(player, reason);
  }

  /** Send a full-state snapshot to one guest (after the last executed frame). */
  resync(player: number): void {
    const ch = this.peers[player];
    const now = this.now();
    if (!ch || now - this.lastResyncAt[player] < RESYNC_COOLDOWN_MS || this.nextExec === 0) return;
    this.lastResyncAt[player] = now;
    this.stats.resyncs++;
    ch.send({ t: 'resync', f: this.nextExec - 1, hold: this.holdUntil, snap: serializeSnapshot(snapshot(this.sim)) });
  }

  broadcast(msg: unknown): void {
    for (const ch of this.peers) ch?.send(msg);
  }

  /** One fixed step: record local input, drop silent guests, finalize and run every frame that's ready. */
  step(local: InputFrame): number {
    const now = this.now();
    const lead = this.localFrame - this.nextExec;
    // After the delay drops, skip sampling every other step until the lead shrinks (edges carry over).
    const shrink = lead > this.delay + 1 && (this.stepCount++ & 1) === 0;
    const w = shrink ? null : this.takeLocal(local);
    if (shrink) this.heldEdges |= quantizeInput(local)[2];
    if (w) {
      // After the delay rises, fill the widened gap once with this input so the host's own frames exist.
      if (this.fillPending) {
        this.fillPending = false;
        while (this.localFrame < this.nextExec + this.delay - 1) this.store[0].set(this.localFrame++, { i: [w[0], w[1], 0], c: [] });
      }
      this.store[0].set(this.localFrame, { i: w, c: this.pendingLocal.splice(0) });
      this.localFrame++;
    }
    for (let p = 1; p < this.playerCount; p++) if (!this.dropped[p] && now - this.lastHeard[p] > DROP_AFTER_MS) this.drop(p, 'timed out');

    let executed = 0;
    this.waitingOn = [];
    while (executed < MAX_CATCHUP && this.nextExec < this.localFrame) {
      const f = this.nextExec;
      const missing: number[] = [];
      if (f >= this.delay) for (let p = 1; p < this.playerCount; p++) if (!this.dropped[p] && !this.store[p].has(f)) missing.push(p);
      // Wait (up to GRACE_MS per stall) only for players who have been keeping
      // up; a player already marked lagging is stood in for immediately, so one
      // slow link adds input delay for that player instead of hitching everyone.
      const waitFor = missing.filter((p) => !this.lagging[p]);
      if (waitFor.length) {
        if (this.waitStart === null) this.waitStart = now;
        if (now - this.waitStart < GRACE_MS) {
          this.waitingOn = waitFor;
          break;
        }
        for (const p of waitFor) this.lagging[p] = true;
      }
      this.waitStart = null;
      for (let p = 1; p < this.playerCount; p++) {
        if (missing.includes(p)) {
          this.onTime[p] = 0;
          this.stats.substitutions++;
        } else if (++this.onTime[p] >= RECOVER_FRAMES) this.lagging[p] = false;
      }

      const inputs: WireInput[] = [];
      const cmds: [number, SimCommand][] = [];
      for (let p = 0; p < this.playerCount; p++) {
        const entry = f < this.delay || this.dropped[p] ? undefined : this.store[p].get(f);
        this.store[p].delete(f);
        let input: WireInput;
        if (entry) {
          input = entry.i;
          this.last[p] = entry.i;
        } else if (p !== 0 && !this.dropped[p] && f >= this.delay) {
          input = [this.last[p][0], this.last[p][1], 0]; // stand-in: keep moving, no edges
        } else input = EMPTY_WIRE_INPUT;
        if (this.carryEdges[p]) {
          input = [input[0], input[1], input[2] | this.carryEdges[p]];
          this.carryEdges[p] = 0;
        }
        for (const c of this.carryCmds[p].splice(0)) cmds.push([p, c]);
        if (entry) for (const c of entry.c) cmds.push([p, c]);
        inputs.push(input);
      }
      this.autoPickForDropped(cmds);
      const bundle: Bundle = { i: inputs, c: cmds };
      this.broadcast(cmds.length ? { t: 'frame', f, i: inputs, c: cmds } : { t: 'frame', f, i: inputs });
      this.execute(f, bundle);
      executed++;
    }
    return executed;
  }

  /** A dropped player can't choose their level-ups; pick for them so the party isn't stuck. */
  private autoPickForDropped(cmds: [number, SimCommand][]): void {
    const sim = this.sim;
    if (sim.run.phase !== 'levelup') return;
    for (const p of sim.pickingPlayers()) {
      if (p <= 0 || !this.dropped[p] || cmds.some(([q]) => q === p)) continue;
      if (sim.offersFor(p).length === 0) continue;
      cmds.push([p, { type: 'chooseUpgrade', player: p, id: botPickUpgrade(sim, p) }]);
    }
  }

  protected hashPoint(f: number, hash: number): void {
    this.hashes.set(f, hash);
    if (this.hashes.size > 40) this.hashes.delete(this.hashes.keys().next().value!);
  }
}

/* -------------------------------------------------------------------------- */
/* Guest                                                                       */
/* -------------------------------------------------------------------------- */

export class LockstepGuest extends LockstepCore {
  private buffer = new Map<number, Bundle>();
  private lastExecAt: number;
  /** Highest frame the host is known to have finalized. */
  private latestReceived = -1;
  private rebuffering = false;
  /** Called after a snapshot resync replaced local state (the view should refresh era/boss). */
  onResync: (() => void) | null = null;

  constructor(sim: Sim, private readonly host: NetChannel, localIndex: number, now: () => number, delay = INPUT_DELAY) {
    super(sim, localIndex, delay, now);
    this.lastExecAt = now();
  }

  /** Route a lockstep message from the host. */
  receive(msg: unknown): void {
    if (!msg || typeof msg !== 'object') return;
    const m = msg as Record<string, unknown>;
    if (m.t === 'frame') {
      const f = m.f;
      if (!Number.isInteger(f) || (f as number) < this.nextExec || !Array.isArray(m.i)) return;
      const inputs = (m.i as unknown[]).map((i) => (isWireInput(i) ? i : EMPTY_WIRE_INPUT));
      const cmds = (Array.isArray(m.c) ? m.c : []).filter((pair): pair is [number, SimCommand] => Array.isArray(pair) && Number.isInteger(pair[0]) && isSimCommand(pair[1]));
      this.buffer.set(f as number, { i: inputs, c: cmds });
      if ((f as number) > this.latestReceived) this.latestReceived = f as number;
    } else if (m.t === 'resync' && Number.isInteger(m.f) && typeof m.snap === 'string') {
      this.applyResync(m.f as number, Number(m.hold) || 0, m.snap);
    } else if (m.t === 'delay' && Number.isInteger(m.d)) {
      this.setDelay(m.d as number);
    }
  }

  /** Bundles received but not yet executed (debug panel). */
  get bufferDepth(): number {
    return this.buffer.size;
  }

  private applyResync(f: number, hold: number, snap: string): void {
    restore(this.sim, deserializeSnapshot(snap));
    this.sim.events.drain();
    this.nextExec = f + 1;
    this.holdUntil = hold;
    if (f > this.latestReceived) this.latestReceived = f;
    if (this.localFrame < this.nextExec) this.localFrame = this.nextExec;
    for (const k of [...this.buffer.keys()]) if (k <= f) this.buffer.delete(k);
    this.stats.resyncs++;
    this.recorder = null; // the input log no longer reproduces this state
    this.onResync?.();
  }

  /** ms since the last bundle ran while none is waiting — the "waiting for host" signal. */
  stalledFor(): number {
    return this.buffer.has(this.nextExec) ? 0 : this.now() - this.lastExecAt;
  }

  step(local: InputFrame): number {
    // A guest that ticks slower than the host (busy machine, background tab)
    // must not keep scheduling input for frames the host has already run: skip
    // past the newest bundle we've seen so input latency stays bounded (the
    // host has already stood in for the skipped frames).
    if (this.localFrame <= this.latestReceived) this.localFrame = this.latestReceived + 1;
    const w = this.takeLocal(local);
    if (w) {
      const c = this.pendingLocal.splice(0);
      this.host.send(c.length ? { t: 'in', f: this.localFrame, i: w, c } : { t: 'in', f: this.localFrame, i: w });
      this.localFrame++;
    }
    // Playout buffer: run one bundle per step at a steady rate. When the buffer
    // runs dry, wait until PLAYOUT_FRAMES are queued (or the wait gets long)
    // before resuming, so jittery arrival becomes a brief pause instead of
    // constant stutter; catch up gently when a burst piles up.
    const depth = this.buffer.size;
    if (this.rebuffering) {
      if (depth >= PLAYOUT_FRAMES || (depth > 0 && this.now() - this.lastExecAt > REBUFFER_MAX_MS)) this.rebuffering = false;
      else return 0;
    }
    const target = depth > 12 ? MAX_CATCHUP : depth > 5 ? 2 : 1;
    let executed = 0;
    while (executed < target) {
      const bundle = this.buffer.get(this.nextExec);
      if (!bundle) break;
      this.buffer.delete(this.nextExec);
      this.execute(this.nextExec, bundle);
      executed++;
    }
    if (executed) this.lastExecAt = this.now();
    if (!this.buffer.has(this.nextExec) && this.stats.frames > 0 && !this.rebuffering) {
      this.rebuffering = true;
      this.stats.rebuffers++;
    }
    return executed;
  }

  protected hashPoint(f: number, hash: number): void {
    this.host.send({ t: 'hash', f, h: hash });
  }
}
